import { useState, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import Dropdown from '../../common/Dropdown'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import ToggleSwitch from '../../common/ToggleSwitch'
import { toast } from '../../common/Toast'
import CheckboxInput from '../../common/CheckboxInput'
import SimpleTextInput from '../../common/SimpleTextInput'
import { getFileIcon } from '../../../utils/functions'
import { languagesShort } from '../../../utils/languages'
import { firmwareUploadWritePermission } from '../../../utils/permissionValidation'
import { updateFirmwareDocument } from '../../../actions/firmware'
import '../uploads.scss'

const EditDocumentFileModal = (props) => {
  const { t } = useTranslation()
  const { selectedFile, closeModal, showSpinner } = props
  const [fileTitle, setFileTitle] = useState('')
  const [languageCode, setLanguageCode] = useState(null)
  const [revision, setRevision] = useState('')
  const [selectedModels, setSelectedModels] = useState([])
  const [modelOptions, setModelOptions] = useState([])
  const [status, setStatus] = useState(false)
  const [haveError, setHaveError] = useState({})
  const statuses = ['inactive', 'active']

  useLayoutEffect(() => {
    if (selectedFile) {
      setStatus(selectedFile.isActive)
      setFileTitle(selectedFile.fileTitle || '')
      setLanguageCode(selectedFile.languageCode || null)
      setRevision(selectedFile.revision || '')
      setSelectedModels(JSON.parse(JSON.stringify(selectedFile.models || [])))

      if (selectedFile.models && selectedFile.models.length && props.prefixes.length) {
        const matchingPrefix = props.prefixes.find((prefix) => prefix.models.includes(selectedFile.models[0]))
        setModelOptions([
          ...new Set(
            props.prefixes
              .filter((prefix) => prefix.productType === matchingPrefix?.productType)
              .map((prefix) => prefix.models)
              .flat(),
          ),
        ])
      }
    }
  }, [selectedFile])

  const fileHeaderContent = () => {
    return (
      <div className="status-wrapper">
        <div className="label">{t('status')}</div>
        <ToggleSwitch checked={status} onChange={() => setStatus((prevValue) => !prevValue)} isDisabled={showSpinner} />
        <div className="status">{t(statuses[Number(status)])}</div>
      </div>
    )
  }

  const fileFooterContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={() => closeModal()}>
          {t('cancel')}
        </SimpleButton>
        <SimpleButton
          className="submit-button"
          onClick={handleEditFile}
          disabled={showSpinner || hasSameInput()}
          requiredPermission={firmwareUploadWritePermission}
        >
          {t('save')}
        </SimpleButton>
      </>
    )
  }

  const hasSameInput = () => {
    return (
      status === selectedFile.isActive &&
      fileTitle === selectedFile.fileTitle &&
      languageCode === selectedFile.languageCode &&
      revision === (selectedFile.revision || '') &&
      selectedModels.length === (selectedFile.models || []).length &&
      selectedModels.every((model) => selectedFile.models?.includes(model))
    )
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (!fileTitle.trim()) {
      errors = { ...errors, fileTitle: true }
      isValid = false
    }

    if (!languageCode) {
      errors = { ...errors, languageCode: true }
      isValid = false
    }

    if (selectedModels.length === 0) {
      errors = { ...errors, models: true }
      isValid = false
    }

    setHaveError(errors)
    return isValid
  }

  const handleEditFile = () => {
    if (!isValidInput()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    const bodyParams = {
      isActive: status,
      fileTitle: fileTitle.trim(),
      languageCode: languageCode,
      revision: revision.trim(),
      models: selectedModels,
    }

    props.actions.updateFirmwareDocument(selectedFile.id, bodyParams).then(() => {
      closeModal()
    })
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={closeModal}
        className="uploads-modal"
        title={t('edit_file')}
        headerContent={fileHeaderContent()}
        footerContent={fileFooterContent()}
      >
        <div className="uploads-modal-content">
          <div className="section-title">{t('general_information').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('file_name')}</p>
              <div className="value">
                {getFileIcon(selectedFile.fileName)}
                {selectedFile.fileName}
              </div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('firmware_context.firmware_type')}</p>
              <div className="value">{selectedFile.firmwareType}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('total_downloads')}</p>
              <div className="value">{selectedFile.totalDownloads || 0}</div>
            </div>
          </div>
          <div className="separator" />
          <div className="section-title">{t('settings').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('file_title')}*`}</p>
              <SimpleTextInput value={fileTitle} onChange={(value) => setFileTitle(value)} onClear={() => setFileTitle('')} hasError={haveError[`fileTitle`]} />
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('language')}*`}</p>
              <Dropdown
                placeholder={t('please_select')}
                options={props.languages.map((languageOption) => ({
                  key: languageOption,
                  value: t(languagesShort[languageOption.toLowerCase()] || languageOption),
                }))}
                initialSelectedKey={languageCode}
                onChange={(lang) => setLanguageCode(lang.key)}
                hasError={haveError[`languageCode`]}
              />
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('document_context.revision_number')}</p>
              <input type="text" className={'name-input'} value={revision || ''} onChange={(e) => setRevision(e.target.value)} />
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{`${t('monitoring.models')}*`}</p>
              {haveError[`models`] && <span className="error-message">{t('error.must_select_one_model')}</span>}
              <div className="ordered-list-wrapper">
                <div className="columns-wrapper">
                  {modelOptions.length > 0
                    ? modelOptions.map((model, index) => (
                        <div className="item-wrapper" key={index}>
                          <CheckboxInput
                            checked={selectedModels.includes(model)}
                            text={model}
                            onChange={() =>
                              setSelectedModels(selectedModels.includes(model) ? selectedModels.filter((m) => m !== model) : [...selectedModels, model])
                            }
                          />
                        </div>
                      ))
                    : selectedModels.map((model, index) => (
                        <div className="item-wrapper" key={index}>
                          <CheckboxInput checked={selectedModels.includes(model)} text={model} disabled />
                        </div>
                      ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageModal>
    </>
  )
}

function stateToProps({ shared, firmware }) {
  return {
    languages: shared?.languages?.map((l) => l.languageCode) || [],
    prefixes: firmware?.prefixes || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        updateFirmwareDocument,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(EditDocumentFileModal)
